import { parseUnits } from 'viem'

import { WalletTokenBalance } from '~/components/forms/deposit/wallet/useWalletDepositBalances'
import { getChain } from '~/constants/chains'
import { isNativeTokenAddress } from '~/utils/tokens'

const MAX_ERROR_MESSAGE_LENGTH = 100

// NumberInput values may carry thousand separators, which parseUnits rejects.
export const tryParseAmount = (
  text: string,
  decimals: number,
): bigint | null => {
  try {
    return parseUnits(text.replace(/,/g, ''), decimals)
  } catch {
    return null
  }
}

export const formatWalletErrorMessage = (error: unknown): string => {
  const message = error instanceof Error ? error.message : 'Transaction failed'
  const lowerMessage = message.toLowerCase()
  if (
    lowerMessage.includes('user rejected') ||
    lowerMessage.includes('user denied')
  ) {
    return 'Request was rejected in the wallet'
  }
  return message.slice(0, MAX_ERROR_MESSAGE_LENGTH)
}

export const getGasSymbol = (chainId: number): string =>
  getChain(chainId)?.nativeCurrency.symbol ?? 'ETH'

// Gas is paid on top of the amount; native sends draw it from the same
// balance, guarded by the full-balance validation in the amount form.
export const getNoGasReason = ({
  token,
  balances,
}: {
  token: Pick<WalletTokenBalance, 'address' | 'chainId'>
  balances: WalletTokenBalance[]
}): string | null => {
  if (isNativeTokenAddress(token.address)) return null

  const hasGas = balances.some(
    (balance) =>
      balance.chainId === token.chainId &&
      isNativeTokenAddress(balance.address) &&
      balance.amount > 0n,
  )
  if (hasGas) return null

  const gasSymbol = getGasSymbol(token.chainId)
  const chainName = getChain(token.chainId)?.name ?? 'this network'
  return `The connected wallet holds no ${gasSymbol} to pay for gas on ${chainName}. Add ${gasSymbol} to it, or copy the address below and send from somewhere else.`
}
